import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      outline: 0;
      transform: translate(-4px, -4px);

      &:after {
        transform: translate(8px, 8px);
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: normal;
      filter: none;
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 14px;
      left: 14px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  // list of recent skills - key is RECENT
  const skills = ['Python', 'C++', 'React', 'TypeScript', 'Next.js'];
  // 'TypeScript', 'React', 'Next.js', 'Eleventy', 'Node.js', 'WordPress'

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
              Hello! I'm Albert, and I've always been driven by a passion for creation and
              adventure, a fascination that developed during my childhood—filled with LEGOs and
              online flash games. The intimacy I felt while exploring the web, playing video games,
              and reading novels was elevated to the next level when I built my first PC. This
              intimacy was something so soothing, yet building that PC fueled curiosity toward the
              technologies that made my experiences possible. I wondered how these pieces of metal
              and plastic were able to bring me the vibrancy of video games, the creativity of
              books, and the connectedness of friendships. I wondered if I could create for others
              the same joy that went into my experiences.
            </p>
            <p>
              Not long after, in 2017-2018, I had my first profound encounter with software
              development through a multi-year series of courses, particularly in the space of game
              development. This unforgettable journey exposed me to the intricate art of designing
              and implementing the functionalities of a game, transforming a vision into a tangible,
              interactive experience. This experience served as a catalyst, inspiring me to embark
              on a journey of studying Computer Science in college. Today, that journey continues
              through my career and projects. I'm excited to keep building, learning, and innovating
              in the world of software development, and I hope this adventure is one that will never
              end.
            </p>

            <p>
              Here are the places I've had the privilege of working at{' '}
              <a href="https://www.dtwc.com/">
                a defense contractor working with radio technologies.
              </a>{' '}
            </p>

            <p>Here are a few technologies I’ve been working with recently:</p>
          </div>

          <ul className="skills-list">
            {skills && skills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.jpg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
